import React, { useState, useEffect, useMemo } from "react";
import { format, addDays } from "date-fns";
import {
  Sparkle, ArrowRight, ArrowLeft, CheckCircle, X, CalendarBlank, Truck,
  Lightning, House, HouseLine, Wrench,
} from "@phosphor-icons/react";
import { PRICING } from "@/lib/site-data";
import { JOB_TYPES, MATERIALS, formatSelectionLine } from "@/lib/materials";
import {
  Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

const WHEN_OPTIONS = [
  { key: "today",    label: "Today",         hint: "ASAP — same day if booked before noon", days: 1 },
  { key: "tomorrow", label: "Tomorrow",      hint: "Next-day delivery", days: 1 },
  { key: "weekend",  label: "This weekend",  hint: "Sat or Sun drop-off", days: null },
  { key: "nextweek", label: "Next week",     hint: "We'll schedule 7 days out", days: 7 },
  { key: "flex",     label: "I'm flexible",  hint: "Best available slot", days: 3 },
];

const JOB_ICONS = {
  roofing: HouseLine,
  renovation: House,
  single: Wrench,
};

function nextSaturday(from = new Date()) {
  const d = new Date(from);
  const day = d.getDay();
  const add = day === 6 ? 7 : 6 - day;
  return addDays(d, add || 1);
}
function recommendDate(whenKey) {
  const now = new Date();
  if (whenKey === "weekend") return nextSaturday(now);
  const opt = WHEN_OPTIONS.find((o) => o.key === whenKey);
  return addDays(now, opt?.days ?? 1);
}

function Progress({ step, total }) {
  return (
    <div className="flex items-center gap-2 w-full" data-testid="wizard-progress">
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 transition-all duration-300 ${i <= step ? "bg-sky-500" : "bg-slate-200"}`}
        />
      ))}
    </div>
  );
}

function ChoiceCard({ active, onClick, title, hint, Icon, testid, badge, thumb }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`group relative text-left bg-white border-2 transition-all duration-150 p-4 sm:p-5 flex items-start gap-4 ${
        active
          ? "border-sky-500 ring-4 ring-sky-200 -translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(14,165,233,0.45)]"
          : "border-slate-900 hover:-translate-y-0.5 hover:bg-slate-50"
      }`}
    >
      {thumb ? (
        <div className="h-14 w-14 border-2 border-slate-900 shrink-0 overflow-hidden bg-slate-100">
          <img src={thumb} alt="" className="h-full w-full object-cover" loading="lazy" />
        </div>
      ) : Icon ? (
        <div
          className={`h-12 w-12 flex items-center justify-center border-2 shrink-0 transition-colors ${
            active ? "bg-sky-500 border-sky-500 text-white" : "bg-slate-900 border-slate-900 text-white"
          }`}
        >
          <Icon size={22} weight="bold" />
        </div>
      ) : null}
      <div className="flex-1 min-w-0">
        {badge && (
          <div className="text-[9px] font-extrabold tracking-[0.22em] uppercase text-sky-600 mb-1">
            {badge}
          </div>
        )}
        <div className="font-display font-bold text-base text-slate-900 leading-tight">
          {title}
        </div>
        {hint && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{hint}</div>}
      </div>
      {active && <CheckCircle size={20} weight="fill" className="shrink-0 text-sky-500 mt-0.5" />}
    </button>
  );
}

export default function HelpMePickWizard({ trigger, defaultOpen = false, onApply }) {
  const [open, setOpen] = useState(defaultOpen);
  const [step, setStep] = useState(0);

  // Step 0: job type
  const [jobKey, setJobKey] = useState(null);
  // Step 1: sub-option (index into the chosen job's options array)
  const [optionIdx, setOptionIdx] = useState(null);
  // Step 2: when
  const [when, setWhen] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(0);
      setJobKey(null);
      setOptionIdx(null);
      setWhen(null);
    }
  }, [open]);

  const job = useMemo(() => JOB_TYPES.find((j) => j.key === jobKey) || null, [jobKey]);
  const option = useMemo(
    () => (job && optionIdx != null ? job.options[optionIdx] : null),
    [job, optionIdx]
  );

  // Effective category/size — for "single" jobs, material lives on the sub-option
  const recMaterial = option?.material || job?.material || null;
  const recSize = option?.size || null;
  const recDate = useMemo(() => (when ? recommendDate(when) : null), [when]);
  const summary = useMemo(() => {
    if (!recMaterial || !recSize) return null;
    return formatSelectionLine(PRICING, recMaterial, recSize);
  }, [recMaterial, recSize]);

  const canNext =
    (step === 0 && !!jobKey) ||
    (step === 1 && optionIdx != null) ||
    (step === 2 && !!recMaterial && !!recSize) ||
    (step === 3 && !!when);

  const STEP_TITLES = [
    "What's the job?",
    job ? `Pick your ${job.label.toLowerCase()} option` : "Pick your option",
    "Your recommended bin",
    "When do you need it?",
  ];

  function next() { if (step < 3) setStep(step + 1); }
  function back() {
    if (step > 0) setStep(step - 1);
    // When going back to step 0, keep jobKey so the user sees their prior pick;
    // but reset option since it belongs to a specific job.
    if (step === 1) setOptionIdx(null);
  }

  function applyRecommendation() {
    if (!recMaterial || !recSize || !recDate) return;
    const payload = {
      category: recMaterial,
      size: recSize,
      tier_index: 0,
      dropoff_date: format(recDate, "yyyy-MM-dd"),
    };
    if (typeof onApply === "function") {
      onApply(payload);
      setOpen(false);
      return;
    }
    const params = new URLSearchParams({
      category: payload.category,
      size: payload.size,
      tier_index: "0",
      dropoff: payload.dropoff_date,
    });
    window.location.href = `/book?${params.toString()}`;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        data-testid="help-me-pick-modal"
        className="max-w-2xl w-[calc(100%-2rem)] p-0 border-2 border-slate-900 rounded-none bg-white sm:rounded-none gap-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <VisuallyHidden.Root>
          <DialogTitle>Help me pick — sizing guide</DialogTitle>
          <DialogDescription>
            A 30-second guided sizing guide with three questions — job type, scope, and timing —
            that recommends the right bin and delivery date.
          </DialogDescription>
        </VisuallyHidden.Root>

        <div className="bg-slate-900 text-white px-6 py-5 flex items-center gap-3 border-b-2 border-slate-900">
          <div className="h-10 w-10 bg-sky-500 flex items-center justify-center shrink-0">
            <Sparkle size={20} weight="fill" className="text-slate-900" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-400">
              Sizing guide · Not sure what size you need?
            </div>
            <div className="font-display font-bold text-base sm:text-lg leading-tight">
              {STEP_TITLES[step]}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="wizard-close"
            aria-label="Close wizard"
            className="h-9 w-9 flex items-center justify-center border-2 border-white/20 hover:bg-white/10 transition-colors"
          >
            <X size={18} weight="bold" />
          </button>
        </div>

        <div className="px-6 pt-5">
          <Progress step={step} total={4} />
          <div className="mt-2 text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">
            Step {Math.min(step + 1, 4)} of 4
          </div>
        </div>

        <div className="px-6 py-6 sm:py-7">
          {step === 0 && (
            <div className="grid gap-3" data-testid="wizard-step-jobtype">
              {JOB_TYPES.map((j) => {
                const Icon = JOB_ICONS[j.key];
                return (
                  <ChoiceCard
                    key={j.key}
                    active={jobKey === j.key}
                    onClick={() => { setJobKey(j.key); setOptionIdx(null); }}
                    title={j.label}
                    hint={j.hint}
                    Icon={Icon}
                    testid={`wizard-job-${j.key}`}
                  />
                );
              })}
            </div>
          )}

          {step === 1 && job && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="wizard-step-option">
              {job.options.map((opt, idx) => {
                const mat = opt.material || job.material;
                const thumb = mat ? MATERIALS[mat]?.photo : null;
                return (
                  <ChoiceCard
                    key={idx}
                    active={optionIdx === idx}
                    onClick={() => setOptionIdx(idx)}
                    title={opt.label}
                    badge={opt.size + (opt.material ? ` · ${opt.material}` : "")}
                    thumb={thumb}
                    testid={`wizard-option-${job.key}-${idx}`}
                  />
                );
              })}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="wizard-result">
              <div className="border-2 border-sky-500 bg-sky-50 p-5">
                <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-600">
                  We&apos;d recommend
                </div>
                <div className="font-display font-black text-2xl sm:text-3xl text-slate-900 mt-1 leading-tight">
                  {recSize} · {recMaterial}
                </div>
                {summary && (
                  <div className="mt-2 text-sm font-semibold text-slate-800">{summary}</div>
                )}
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">
                  Best fit for your scope. You can change anything on the booking page before paying.
                </p>

                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  <div className="border-2 border-slate-900 bg-white p-3">
                    <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-slate-500">Bin</div>
                    <div className="font-display font-bold text-slate-900 mt-1">{recSize}</div>
                  </div>
                  <div className="border-2 border-slate-900 bg-white p-3">
                    <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-slate-500">Material</div>
                    <div className="font-display font-bold text-slate-900 mt-1">{recMaterial}</div>
                  </div>
                </div>
              </div>
              <div className="text-sm text-slate-600 leading-relaxed">
                Next: tell us <span className="font-bold text-slate-900">when</span> you need it,
                then we&apos;ll drop you straight onto this bin in the booking flow.
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="wizard-step-when">
              <div className="grid sm:grid-cols-2 gap-3">
                {WHEN_OPTIONS.map((opt) => (
                  <ChoiceCard
                    key={opt.key}
                    active={when === opt.key}
                    onClick={() => setWhen(opt.key)}
                    title={opt.label}
                    hint={opt.hint}
                    Icon={opt.key === "today" ? Lightning : CalendarBlank}
                    testid={`wizard-when-${opt.key}`}
                  />
                ))}
              </div>

              {when && recDate && (
                <div className="border-2 border-emerald-500 bg-emerald-50 px-4 py-3 text-sm text-slate-800">
                  <span className="font-bold">You&apos;re booking:</span> {recSize} · {recMaterial} for{" "}
                  <span className="font-bold">{format(recDate, "EEE, MMM d")}</span>. We&apos;ll match the exact time window on the booking page.
                </div>
              )}

              <button
                type="button"
                onClick={applyRecommendation}
                disabled={!when}
                data-testid="wizard-apply"
                className="w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm uppercase tracking-[0.18em] border-2 border-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Truck size={16} weight="bold" />
                Use this recommendation
                <ArrowRight size={16} weight="bold" />
              </button>
              <div className="text-xs text-slate-500 text-center">
                Not quite right? Tap <span className="font-bold text-slate-900">Back</span> to tweak any answer.
              </div>
            </div>
          )}
        </div>

        <div className="border-t-2 border-slate-900 bg-slate-50 px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            data-testid="wizard-back"
            className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-slate-700 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={14} weight="bold" /> Back
          </button>

          {step < 3 ? (
            <button
              type="button"
              onClick={next}
              disabled={!canNext}
              data-testid="wizard-next"
              className="inline-flex items-center gap-2 px-5 py-3 bg-slate-900 text-white font-bold text-xs uppercase tracking-[0.18em] border-2 border-slate-900 hover:bg-sky-500 hover:border-sky-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {step === 1 ? "See recommendation" : step === 2 ? "Pick date" : "Next"} <ArrowRight size={14} weight="bold" />
            </button>
          ) : (
            <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-slate-500">
              Ready to book
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
